/**
 * Easter mug designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterMugDesignsPack: ToilePack = {
    slug: 'easter-mug-designs',
    category: 'season',
    name: 'Easter mug designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Spring slogan', subject: 'The words "SOME BUNNY LOVES YOU" lettered in a rounded script with a small heart, isolated'},
        {key: 'chick', label: 'Chick', subject: 'A round yellow chick with a flower crown, isolated on a plain ground, no text'},
        {key: 'band', label: 'Pattern band', subject: 'A horizontal band of small eggs and tulips sized to wrap a mug, isolated, no text'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'Soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'Faded rose, sage green, cream and a little gold', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'Two or three flat spring colours on white', framing: 'Centered with generous white space around it'},
    ],
    directions: ['as a travel tumbler instead', 'with initials added', 'in a single spot color'],
    directionHint: 'try: as a travel tumbler instead',
    seo: {
        title: 'Easter Mug Designs: Slogan, Chick & Pattern Band',
        metaDescription: 'Nine Easter mug wrap designs — Some Bunny Loves You slogan, chick, egg-and-tulip band — in pastel kawaii, vintage, and modern minimal styles.',
        h1: 'Easter mug design: some-bunny slogan, chick, pattern band',
        tagline: 'Nine designs, three graphics, three treatments each.',
        intro: 'Nine flat artwork designs built to wrap a mug, shown isolated with no mug in frame: a Some Bunny Loves You slogan with a small heart, a round yellow chick with a flower crown, and a repeating band of eggs and tulips, each in a pastel kawaii, a Victorian vintage, and a modern minimal treatment. Ready to apply to any mug template or print service.',
        useCases: [
            'a print-on-demand mug listing',
            'a sublimation mug wrap at home',
            'a gift shop or craft fair mug',
            'a matching coaster or napkin set',
        ],
        faq: [
            {q: 'Does the design already show it applied to a mug?', a: 'No — each file is the flat artwork alone, isolated on a plain background, so you can apply it to any mug mockup, wrap template, or print service yourself.'},
            {q: 'Is the pattern band sized to wrap all the way around a mug?', a: 'Yes — the band design is composed as a horizontal repeating strip, proportioned for a standard mug wrap.'},
            {q: "What's the difference between the three styles?", a: 'Pastel kawaii uses rounded, friendly shapes in soft colour, vintage draws on Victorian Easter-card illustration, and modern minimal keeps to a few flat shapes and generous space.'},
            {q: 'Can I use these designs for a mug I sell commercially?', a: 'Yes — the designs are cleared for commercial use, including print-on-demand and craft-fair sales.'},
        ],
    },
}
